import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, RefreshCw } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../ui';
import { useAuditActionsCatalog, useAuditEvents } from '../../hooks/useAuditEvents';
import { useProjects } from '../../hooks/useProjects';
import type { AuditEvent, AuditEventsFilters } from '../../services/auditEventsApi';
import { auditEventsApi } from '../../services/auditEventsApi';
import './AuditLogPage.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function AuditLogPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [filters, setFilters] = useState<AuditEventsFilters>({ limit: 50 });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: catalog } = useAuditActionsCatalog(orgId);
  const { data: projects } = useProjects(orgId);
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading, refetch } =
    useAuditEvents(orgId, filters);

  const rows = useMemo(() => {
    return data?.pages.flatMap(p => p.data) ?? [];
  }, [data]);

  const projectNamesById = useMemo(() => {
    const map = new Map<string, string>();
    projects?.forEach(p => map.set(p.id, p.name));
    return map;
  }, [projects]);

  const handleFilterChange = (patch: Partial<AuditEventsFilters>) => {
    setFilters(prev => ({ ...prev, ...patch }));
  };

  const handleExportCsv = () => {
    if (!orgId) return;
    const url = `${API_BASE_URL}${auditEventsApi.csvUrl(orgId, filters)}`;
    window.location.assign(url);
  };

  if (!orgId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Pick an organization to view its audit log."
      />
    );
  }

  return (
    <div className="audit-log-page">
      <header className="audit-log-page__header">
        <div>
          <h1>Audit log</h1>
          <p className="audit-log-page__subtitle">
            Immutable record of every auth event and admin action in this organization.
          </p>
        </div>
        <div className="audit-log-page__actions">
          <Button variant="ghost" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={14} /> Refresh
          </Button>
          <Button variant="secondary" onClick={handleExportCsv}>
            <Download size={14} /> Export CSV
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <h2>Filters</h2>
        </CardHeader>
        <CardBody>
          <div className="audit-log-page__filters">
            <Field label="Action">
              <select
                className="audit-log-page__select"
                value={filters.action?.[0] ?? ''}
                onChange={e =>
                  handleFilterChange({
                    action: e.target.value ? [e.target.value] : undefined,
                  })
                }
              >
                <option value="">All actions</option>
                {catalog &&
                  Object.entries(catalog.categories).map(([category, actions]) => (
                    <optgroup key={category} label={category}>
                      {actions.map(a => (
                        <option key={a.action} value={a.action}>
                          {a.action}
                        </option>
                      ))}
                    </optgroup>
                  ))}
              </select>
            </Field>
            <Field label="Actor type">
              <select
                className="audit-log-page__select"
                value={filters.actor_type ?? ''}
                onChange={e => handleFilterChange({ actor_type: e.target.value || undefined })}
              >
                <option value="">Any</option>
                {catalog?.actor_types.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Project">
              <select
                className="audit-log-page__select"
                value={filters.project_id ?? ''}
                onChange={e => handleFilterChange({ project_id: e.target.value || undefined })}
              >
                <option value="">All projects</option>
                {projects?.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="From">
              <Input
                type="datetime-local"
                value={filters.from ?? ''}
                onChange={e => handleFilterChange({ from: e.target.value || undefined })}
              />
            </Field>
            <Field label="To">
              <Input
                type="datetime-local"
                value={filters.to ?? ''}
                onChange={e => handleFilterChange({ to: e.target.value || undefined })}
              />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {isLoading && <div className="audit-log-page__loading">Loading…</div>}
          {!isLoading && rows.length === 0 && (
            <EmptyState
              title="No audit events"
              description="Matching events will appear here as admins and end users perform actions."
            />
          )}
          {rows.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Time</TableHeaderCell>
                  <TableHeaderCell>Action</TableHeaderCell>
                  <TableHeaderCell>Actor</TableHeaderCell>
                  <TableHeaderCell>Project</TableHeaderCell>
                  <TableHeaderCell>Resource</TableHeaderCell>
                  <TableHeaderCell>IP</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(event => (
                  <AuditRow
                    key={event.id}
                    event={event}
                    projectName={
                      event.project_id ? projectNamesById.get(event.project_id) : undefined
                    }
                    expanded={expandedId === event.id}
                    onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {hasNextPage && (
        <div className="audit-log-page__load-more">
          <Button variant="secondary" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
}

interface AuditRowProps {
  event: AuditEvent;
  projectName: string | undefined;
  expanded: boolean;
  onToggle: () => void;
}

function AuditRow({ event, projectName, expanded, onToggle }: AuditRowProps) {
  const projectCell = event.project_id ? (projectName ?? shortId(event.project_id)) : '—';

  return (
    <>
      <TableRow onClick={onToggle} className="audit-log-page__row">
        <TableCell>{new Date(event.created_at).toLocaleString()}</TableCell>
        <TableCell>
          <Badge tone="neutral">{event.action}</Badge>
        </TableCell>
        <TableCell>
          {event.actor_label ?? event.actor_type}
          <span className="audit-log-page__muted"> · {event.actor_type}</span>
        </TableCell>
        <TableCell>{projectCell}</TableCell>
        <TableCell>
          {event.resource_type
            ? `${event.resource_type}${event.resource_id ? ` ${shortId(event.resource_id)}` : ''}`
            : '—'}
        </TableCell>
        <TableCell>{event.ip ?? '—'}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6}>
            <dl className="audit-log-page__detail">
              <dt>Description</dt>
              <dd>{event.description}</dd>
              <dt>Request ID</dt>
              <dd>{event.request_id ?? '—'}</dd>
              <dt>User agent</dt>
              <dd>{event.user_agent ?? '—'}</dd>
              <dt>Metadata</dt>
              <dd>
                <pre>{JSON.stringify(event.metadata, null, 2)}</pre>
              </dd>
            </dl>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function shortId(id: string) {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}
